import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { dispatchAPI, uploadAPI, salesPipelineAPI, orderAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import OrderReleaseInfo from '../../components/common/OrderReleaseInfo';
import Pagination from '../../components/common/Pagination';
import OrderWorkflowBanner, { OrderStepBadge } from '../../components/common/OrderWorkflowBanner';
import {
  DispatchQcModal, QcStatusBadge, QcActionButton,
  canRunDispatchQc, isQcCleared
} from '../../components/common/DispatchQualityCheck';
import {
  HiOutlineTruck, HiOutlinePlus, HiOutlineSearch, HiOutlineClipboardList,
  HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineClock, HiOutlineExclamation, HiOutlineDocumentText,
  HiOutlineSave, HiOutlineX, HiOutlineUpload, HiOutlinePencil, HiOutlineTrash,
  HiOutlineRefresh
} from 'react-icons/hi';



// ── Reusable Modal Overlay ─────────────────────────────────────────
const ModalOverlay = ({ onClose, children, title }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px'
  }} onClick={e => e.target === e.currentTarget && onClose()}>
    <div style={{
      background: 'var(--bg-card)', borderRadius: '16px', width: '100%', maxWidth: '600px',
      maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid var(--border)',
      boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
    }}>
      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>{title}</h2>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: '1.4rem' }}><HiOutlineX /></button>
      </div>
      <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>{children}</div>
    </div>
  </div>
);

const formatExternalUrl = (url) => {
  if (!url) return '#';
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (trimmed.startsWith('/uploads/') || trimmed.startsWith('/api/uploads/')) {
    const cleanPath = trimmed.startsWith('/api/') ? trimmed : `/api${trimmed}`;
    const apiURL = process.env.REACT_APP_API_URL;
    if (apiURL) {
      const base = apiURL.endsWith('/api') ? apiURL.slice(0, -4) : (apiURL.endsWith('/api/') ? apiURL.slice(0, -5) : apiURL);
      return `${base}${cleanPath}`;
    }
    return window.location.port
      ? `${window.location.protocol}//${window.location.hostname}:9898${cleanPath}`
      : `${window.location.origin}${cleanPath}`;
  }
  return `https://${trimmed}`;
};

const formatDateTime = (dateStr) => {

  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

// ── Status config ─────────────────────────────────────────────────

const STATUS_CONFIG = {
  assemble:   { label: 'Assembling',  color: '#f59e0b', bg: 'rgba(245,158,11,0.15)', icon: '🔧' },
  hold:       { label: 'On Hold',     color: '#ef4444', bg: 'rgba(239,68,68,0.15)',  icon: '⏸️' },
  packing:    { label: 'Packing',     color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', icon: '📦' },
  dispatched: { label: 'Dispatched',  color: '#22c55e', bg: 'rgba(34,197,94,0.15)',  icon: '✅' },
};


const BLANK_FORM = {
  customerName: '', customerContact: '', customerAddress: '', salesOrderRef: '',
  items: [{ modelNumber: '', description: '', quantity: 1, unit: 'pcs' }],
  status: 'assemble', courier: '', trackingNumber: '', lrNumber: '',
  lrCopyUrl: '', podUrl: '', holdReason: '', packingDetails: '',
  totalBoxes: '', totalWeight: '', remarks: ''
};

export default function DispatchDashboard() {
  const { user } = useAuth();
  const canApproveGate = ['super_admin', 'admin', 'gate_guard'].includes(user?.role);
  const canEditDispatch = user?.role !== 'gate_guard';


  // Pre-dispatch Quality Check (meters only)
  const canRunQc = canRunDispatchQc(user);
  const [showQcModal, setShowQcModal] = useState(false);
  const [qcOrder, setQcOrder] = useState(null);

  const [dispatches, setDispatches] = useState([]);
  const [stats, setStats] = useState({ assemble: 0, hold: 0, packing: 0, dispatched: 0, today: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dispatchPage, setDispatchPage] = useState(1);
  const [salesOrders, setSalesOrders] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [dRes, sRes, pipelineRes, orderRes] = await Promise.all([
        dispatchAPI.getAll(filterStatus ? { status: filterStatus } : {}).catch(err => {
          console.error('[Dispatch getAll error]:', err);
          return { data: { data: [] } };
        }),
        dispatchAPI.getStats().catch(err => {
          console.error('[Dispatch getStats error]:', err);
          return { data: { data: { assemble: 0, hold: 0, packing: 0, dispatched: 0, today: 0 } } };
        }),
        salesPipelineAPI.getAll().catch(() => ({ data: { data: [] } })),
        orderAPI.getOrders().catch(() => ({ data: { data: [] } }))
      ]);
      setDispatches(Array.isArray(dRes.data?.data) ? dRes.data.data : (Array.isArray(dRes.data) ? dRes.data : []));
      setStats(sRes.data?.data || { assemble: 0, hold: 0, packing: 0, dispatched: 0, today: 0 });

      const pipelineList = pipelineRes.data?.data || [];
      const ordersList = Array.isArray(orderRes.data?.data) ? orderRes.data.data : (Array.isArray(orderRes.data) ? orderRes.data : []);

      const combined = [];

      pipelineList.forEach(p => {
        // Sales Orders reach the store only after Admin approval
        if (p.stage === 'sales_order' && p.approvalStatus !== 'approved') return;
        const refNo = p.salesOrderNumber || p.quotationNumber || p.piNumber || (p._id ? `SO-${p._id.slice(-6).toUpperCase()}` : '');
        if (refNo && !combined.some(c => c.salesOrderRef === refNo)) {
          combined.push({
            source: 'pipeline',
            id: p._id,
            salesOrderRef: refNo,
            customerName: p.customerName || '',
            customerContact: p.customerContact || '',
            customerEmail: p.customerEmail || '',
            customerAddress: p.customerAddress || '',
            stage: p.stage,
            items: (p.items || []).map(it => ({
              modelNumber: it.modelNumber || it.dragChainConfig?.modelNumber || '',
              description: it.description || (it.dragChainConfig ? `${it.dragChainConfig.width || ''} R${it.dragChainConfig.radius || ''} ${it.dragChainConfig.chainType || ''}` : ''),
              quantity: Number(it.quantity) || 1,
              unit: it.unit || 'meters'
            }))
          });
        }
      });

      ordersList.forEach(o => {
        const refNo = o.orderNumber || (o._id ? `ORD-${o._id.slice(-6).toUpperCase()}` : '');
        if (refNo && !combined.some(c => c.salesOrderRef === refNo)) {
          combined.push({
            source: 'orders',
            id: o._id,
            salesOrderRef: refNo,
            customerName: o.clientName || '',
            customerContact: '',
            customerEmail: '',
            customerAddress: '',
            stage: o.status,
            items: [{
              modelNumber: o.modelNumber || '',
              description: `Order ${o.orderNumber || ''}`,
              quantity: Number(o.orderQuantity) || 1,
              unit: 'meters'
            }]
          });
        }
      });

      setSalesOrders(combined);
    } catch { toast.error('Failed to load dispatch data'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  const handleSelectSalesOrder = (refNumber) => {
    if (!refNumber) {
      setForm(p => ({ ...p, salesOrderRef: '' }));
      return;
    }
    if (refNumber === '__custom__') {
      setForm(p => ({ ...p, salesOrderRef: '' }));
      return;
    }

    const selectedSO = salesOrders.find(so => so.salesOrderRef === refNumber);
    if (selectedSO) {
      setForm(p => ({
        ...p,
        salesOrderRef: selectedSO.salesOrderRef,
        customerName: selectedSO.customerName || p.customerName,
        customerContact: selectedSO.customerContact || p.customerContact,
        customerAddress: selectedSO.customerAddress || p.customerAddress,
        items: selectedSO.items && selectedSO.items.length > 0 
          ? selectedSO.items.map(it => ({
              modelNumber: it.modelNumber || '',
              description: it.description || '',
              quantity: Number(it.quantity) || 1,
              unit: it.unit || 'meters'
            }))
          : p.items
      }));
      toast.success(`Loaded items & details from Sales Order: ${selectedSO.salesOrderRef}!`);
    } else {
      setForm(p => ({ ...p, salesOrderRef: refNumber }));
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGatePass = async (id, gateStatus) => {
    let gateRemarks = '';
    if (gateStatus === 'declined') {
      gateRemarks = window.prompt('Enter reason for declining/holding vehicle at gate:') || 'Failed Gate Inspection';
      if (!gateRemarks) return;
    }
    try {
      await dispatchAPI.update(id, { gateStatus, gateRemarks });
      toast.success(gateStatus === 'approved' ? '✅ Gate Outbound Pass Approved!' : '❌ Gate Pass Declined / Stopped');
      fetchData();
    } catch (err) {
      toast.error('Failed to update Gate Approval status');
    }
  };


  const openCreate = () => { setForm(BLANK_FORM); setEditingId(null); setModalStep(1); setShowModal(true); };
  const openEdit = (d) => {
    setForm({
      customerName: d.customerName || '', customerContact: d.customerContact || '',
      customerAddress: d.customerAddress || '', salesOrderRef: d.salesOrderRef || '',
      items: d.items?.length ? d.items : BLANK_FORM.items,
      status: d.status || 'assemble', courier: d.courier || '',
      trackingNumber: d.trackingNumber || '', lrNumber: d.lrNumber || '',
      lrCopyUrl: d.lrCopyUrl || '', podUrl: d.podUrl || '',
      holdReason: d.holdReason || '', packingDetails: d.packingDetails || '',
      totalBoxes: d.totalBoxes || '', totalWeight: d.totalWeight || '',
      remarks: d.remarks || ''
    });
    setEditingId(d._id);
    setModalStep(1);
    setShowModal(true);
  };

  const handleSaveDraft = async (e) => {
    if (e) e.preventDefault();
    if (!form.customerName?.trim()) {
      toast.error('Please enter Customer Name to save draft');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await dispatchAPI.update(editingId, form);
        toast.success('💾 Draft saved successfully!');
      } else {
        const res = await dispatchAPI.create(form);
        const createdId = res.data?.data?._id || res.data?._id;
        if (createdId) setEditingId(createdId);
        toast.success('💾 Saved as draft!');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save draft');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await dispatchAPI.update(editingId, form);
        toast.success('Dispatch order updated');
      } else {
        await dispatchAPI.create(form);
        toast.success('Dispatch order created');
      }
      setShowModal(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dispatch order?')) return;
    try { await dispatchAPI.delete(id); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      toast.loading('Uploading file...', { id: 'doc-upload' });
      const res = await uploadAPI.uploadFile(formData);
      if (res.data && res.data.fileUrl) {
        setForm(p => ({ ...p, [field]: res.data.fileUrl }));
        toast.success('Document uploaded successfully!', { id: 'doc-upload' });
      } else {
        toast.error('Upload failed', { id: 'doc-upload' });
      }
    } catch (err) {
      toast.error('Failed to upload file', { id: 'doc-upload' });
    }
  };

  const handleOpenQcModal = (order) => {
    setQcOrder(order);
    setShowQcModal(true);
  };

  const handleCloseQcModal = () => {
    setShowQcModal(false);
    setQcOrder(null);
  };

  const handleStatusChange = async (id, status) => {
    // Mandatory pre-dispatch QC gate: block Packing / Dispatched client-side too
    const target = dispatches.find(d => d._id === id);
    if (['packing', 'dispatched'].includes(status) && target && !isQcCleared(target)) {
      toast.error(
        `Quality check required before ${STATUS_CONFIG[status].label}. ` +
        (target.qcStatus === 'failed'
          ? 'The last check FAILED — pass a re-check first.'
          : 'The pre-dispatch quality check is still pending.'),
        { duration: 6000 }
      );
      if (canRunQc) handleOpenQcModal(target);
      return;
    }

    try {
      await dispatchAPI.update(id, { status });
      toast.success(`Moved to ${STATUS_CONFIG[status].label}`);
      fetchData();
    } catch (err) {
      if (err.response?.data?.qcBlocked) {
        toast.error(err.response.data.message, { duration: 6000 });
        if (canRunQc && target) handleOpenQcModal(target);
        return;
      }
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const updateItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: val };
    setForm(p => ({ ...p, items }));
  };
  const addItem = () => setForm(p => ({ ...p, items: [...p.items, { modelNumber: '', description: '', quantity: 1, unit: 'pcs' }] }));
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const filtered = dispatches.filter(d =>
    d.customerName?.toLowerCase().includes(search.toLowerCase()) ||
    d.salesOrderRef?.toLowerCase().includes(search.toLowerCase()) ||
    d.lrNumber?.toLowerCase().includes(search.toLowerCase())
  );

  const statCards = [
    { key: 'assemble',   label: 'Assembling',  ...STATUS_CONFIG.assemble  },
    { key: 'hold',       label: 'On Hold',      ...STATUS_CONFIG.hold      },
    { key: 'packing',    label: 'Packing',      ...STATUS_CONFIG.packing   },
    { key: 'dispatched', label: 'Dispatched',   ...STATUS_CONFIG.dispatched},
  ];

  return (
    <DashboardLayout pageTitle="Dispatch & Shipping Logistics">


      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <HiOutlineTruck style={{ color: 'var(--primary)' }} /> Dispatch & Fulfillment Management
          </h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.9rem' }}>
            Track end-to-end flow: Sales ➔ Confirmed Order ➔ (Store) Assembly ➔ Box Packing ➔ Logistics Dispatch
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={fetchData} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <HiOutlineRefresh /> Refresh
          </button>
          {canEditDispatch && (
            <button onClick={openCreate} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <HiOutlinePlus /> New Dispatch
            </button>
          )}
        </div>
      </div>

      {/* End-to-End 9-Step Lifecycle Banner */}
      <OrderWorkflowBanner activeStageKey={filterStatus || 'assemble'} />

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        {statCards.map(s => (
          <div key={s.key} onClick={() => setFilterStatus(filterStatus === s.key ? '' : s.key)}
            style={{
              background: filterStatus === s.key ? s.bg : 'var(--bg-card)',
              border: `1px solid ${filterStatus === s.key ? s.color : 'var(--border)'}`,
              borderRadius: '12px', padding: '20px', cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
            <div style={{ fontSize: '2rem', marginBottom: '4px' }}>{s.icon}</div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: s.color }}>{stats[s.key] || 0}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>{s.label}</div>
          </div>
        ))}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
          <div style={{ fontSize: '2rem', marginBottom: '4px' }}>📅</div>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--primary)' }}>{stats.today || 0}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Today's Orders</div>
        </div>
      </div>

      {/* Filter & Search */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
          <HiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
          <input className="form-input" style={{ paddingLeft: '36px' }} placeholder="Search customer, order ref, LR no..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" style={{ width: '180px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Dispatch Table */}
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '1100px', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                {(canEditDispatch
                  ? ['Customer', 'Sales Order Ref', 'Items', 'Status', 'Quality Check (Meters)', 'LR No.', 'Courier', 'Tracking', 'POD', 'Gate Outbound Pass', 'Actions']
                  : ['Customer', 'Sales Order Ref', 'Items', 'Status', 'Quality Check (Meters)', 'LR No.', 'Courier', 'Tracking', 'POD', 'Gate Outbound Pass']
                ).map(h => (
                  <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-dim)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={canEditDispatch ? 11 : 10} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={canEditDispatch ? 11 : 10} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)' }}>
                  <HiOutlineTruck style={{ fontSize: '2.5rem', marginBottom: '8px', display: 'block', margin: '0 auto 8px' }} />
                  No dispatch orders found
                </td></tr>
              ) : filtered.slice((dispatchPage - 1) * 10, dispatchPage * 10).map(d => {
                const sc = STATUS_CONFIG[d.status] || STATUS_CONFIG.assemble;
                return (
                  <tr key={d._id} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600 }}>{d.customerName}</div>
                      {d.customerContact && <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>📞 {d.customerContact}</div>}
                      {d.createdAt && (
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '3px' }}>
                          🕒 Created: {formatDateTime(d.createdAt)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                      {d.salesOrderRef || '—'}
                      <OrderReleaseInfo job={d} />
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      {d.items?.slice(0, 2).map((it, i) => (
                        <div key={i} style={{ fontSize: '0.82rem' }}>{it.modelNumber || it.description} × {it.quantity}</div>
                      ))}
                      {d.items?.length > 2 && <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>+{d.items.length - 2} more</div>}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <div style={{ marginBottom: '4px' }}>
                        <OrderStepBadge record={d} />
                      </div>
                      {user?.role === 'gate_guard' ? (
                        <span style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.color}`, borderRadius: '8px', padding: '4px 10px', fontSize: '0.82rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          {sc.icon} {sc.label}
                        </span>
                      ) : (
                        <select value={d.status} onChange={e => handleStatusChange(d._id, e.target.value)}
                          style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.color}`, borderRadius: '8px', padding: '4px 8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>
                          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
                        </select>
                      )}
                    </td>

                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <QcStatusBadge order={d} showInspector />
                      {canRunQc && (
                        <div style={{ marginTop: '6px' }}>
                          <QcActionButton order={d} onClick={handleOpenQcModal} />
                        </div>
                      )}
                    </td>

                    <td style={{ padding: '12px 16px', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{d.lrNumber || '—'}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{d.courier || '—'}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{d.trackingNumber || '—'}</td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      {d.podUrl
                        ? <a href={formatExternalUrl(d.podUrl)} target="_blank" rel="noreferrer" style={{ color: '#22c55e', fontSize: '0.82rem', fontWeight: 700, textDecoration: 'none' }}>✅ View POD</a>
                        : <span style={{ color: 'var(--text-dim)', fontSize: '0.82rem' }}>Pending</span>}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      {canApproveGate ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {d.gateStatus === 'approved' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid #22c55e', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                  <HiOutlineCheckCircle style={{ fontSize: '1rem' }} /> Approved
                                </span>
                                <button onClick={() => handleGatePass(d._id, 'declined')} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}>
                                  Reject
                                </button>
                              </div>
                              {d.gateApprovedBy?.name && (
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                                  By: {d.gateApprovedBy.name}
                                </div>
                              )}
                              {d.gateApprovedAt && (
                                <div style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 600 }}>
                                  🕒 {formatDateTime(d.gateApprovedAt)}
                                </div>
                              )}
                            </div>
                          ) : d.gateStatus === 'declined' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', padding: '4px 10px', fontSize: '0.8rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                  <HiOutlineXCircle style={{ fontSize: '1rem' }} /> Declined
                                </span>
                                <button onClick={() => handleGatePass(d._id, 'approved')} style={{ background: 'none', border: 'none', color: '#10b981', fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline' }}>
                                  Re-Approve
                                </button>
                              </div>
                              {d.gateRemarks && <div style={{ fontSize: '0.72rem', color: '#ef4444' }}>Reason: {d.gateRemarks}</div>}
                              {d.gateApprovedBy?.name && (
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>
                                  By: {d.gateApprovedBy.name}
                                </div>
                              )}
                              {d.gateApprovedAt && (
                                <div style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 600 }}>
                                  🕒 {formatDateTime(d.gateApprovedAt)}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '110px' }}>
                              <button
                                onClick={() => handleGatePass(d._id, 'approved')}
                                style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%' }}
                              >
                                <HiOutlineCheckCircle style={{ fontSize: '1rem' }} /> Approve
                              </button>
                              <button
                                onClick={() => handleGatePass(d._id, 'declined')}
                                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', width: '100%' }}
                              >
                                <HiOutlineXCircle style={{ fontSize: '1rem' }} /> Decline
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {d.gateStatus === 'approved' ? (
                            <>
                              <span style={{ background: 'rgba(34,197,94,0.15)', color: '#22c55e', border: '1px solid #22c55e', borderRadius: '6px', padding: '4px 8px', fontSize: '0.78rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                <HiOutlineCheckCircle style={{ fontSize: '0.9rem' }} /> Gate Passed
                              </span>
                              {d.gateApprovedBy?.name && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>By: {d.gateApprovedBy.name}</div>}
                              {d.gateApprovedAt && <div style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 600 }}>🕒 {formatDateTime(d.gateApprovedAt)}</div>}
                            </>
                          ) : d.gateStatus === 'declined' ? (
                            <>
                              <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '6px', padding: '4px 8px', fontSize: '0.78rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                <HiOutlineXCircle style={{ fontSize: '0.9rem' }} /> Stopped at Gate
                              </span>
                              {d.gateRemarks && <div style={{ fontSize: '0.72rem', color: '#ef4444' }}>Reason: {d.gateRemarks}</div>}
                              {d.gateApprovedBy?.name && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>By: {d.gateApprovedBy.name}</div>}
                              {d.gateApprovedAt && <div style={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 600 }}>🕒 {formatDateTime(d.gateApprovedAt)}</div>}
                            </>
                          ) : (
                            <span style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid #f59e0b', borderRadius: '6px', padding: '4px 8px', fontSize: '0.78rem', fontWeight: 700, display: 'inline-block', whiteSpace: 'nowrap' }}>
                              ⏳ Gate Check Pending
                            </span>
                          )}
                        </div>
                      )}
                    </td>



                    {canEditDispatch && (
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button onClick={() => openEdit(d)} className="btn btn-secondary" style={{ padding: '5px 8px', fontSize: '0.8rem' }}><HiOutlinePencil /></button>
                          <button onClick={() => handleDelete(d._id)} style={{ padding: '5px 8px', fontSize: '0.8rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '6px', cursor: 'pointer' }}><HiOutlineTrash /></button>
                        </div>
                      </td>
                    )}


                  </tr>
                );
              })}

            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={dispatchPage}
          totalPages={Math.ceil(filtered.length / 10) || 1}
          totalItems={filtered.length}
          itemsPerPage={10}
          onPageChange={setDispatchPage}
        />
      </div>

      {/* Create/Edit Modal - Multi-Step Wizard */}
      {showModal && (
        <ModalOverlay onClose={() => setShowModal(false)} title={editingId ? 'Edit Dispatch Order' : 'New Order for Assembly & Dispatch'}>
          {/* Step Navigator */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '20px',
            padding: '6px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid var(--border)'
          }}>
            {[
              { step: 1, label: '1. Assembly', icon: '🔧', desc: 'Order & Links' },
              { step: 2, label: '2. Packing', icon: '📦', desc: 'Boxes & Wt' },
              { step: 3, label: '3. Billing', icon: '🧾', desc: 'Invoices' },
              { step: 4, label: '4. Dispatch', icon: '🚚', desc: 'LR & Courier' }
            ].map(s => {
              const isActive = modalStep === s.step;
              const isPassed = modalStep > s.step;
              return (
                <button
                  key={s.step}
                  type="button"
                  onClick={() => {
                    if (s.step > 1 && !form.customerName?.trim()) {
                      toast.error('Please enter Customer Name first');
                      return;
                    }
                    setModalStep(s.step);
                  }}
                  style={{
                    border: isActive ? '1.5px solid var(--primary)' : '1px solid transparent',
                    background: isActive ? 'rgba(99,102,241,0.15)' : (isPassed ? 'rgba(34,197,94,0.08)' : 'transparent'),
                    color: isActive ? 'var(--primary)' : (isPassed ? '#10b981' : 'var(--text-dim)'),
                    borderRadius: '8px', padding: '8px 4px', cursor: 'pointer', textAlign: 'center',
                    transition: 'all 0.15s'
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: '0.8rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    <span>{s.icon}</span> <span>{s.label}</span>
                  </div>
                  <div style={{ fontSize: '0.68rem', opacity: 0.8, marginTop: '2px' }}>{s.desc}</div>
                </button>
              );
            })}
          </div>

          <form onSubmit={handleSubmit}>
            {/* STEP 1: ASSEMBLY & ORDER DETAILS */}
            {modalStep === 1 && (
              <div>
                <div style={{ marginBottom: '14px' }}>
                  <label className="form-label">Customer Name *</label>
                  <input className="form-input" required value={form.customerName} onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))} placeholder="e.g. ABC Industries" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label className="form-label">Contact / Phone</label>
                    <input className="form-input" value={form.customerContact} onChange={e => setForm(p => ({ ...p, customerContact: e.target.value }))} placeholder="+91 98765..." />
                  </div>
                  <div>
                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Sales Order Ref *</span>
                      {form.salesOrderRef && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--primary)', fontWeight: 600 }}>
                          ✓ {form.salesOrderRef}
                        </span>
                      )}
                    </label>
                    <select
                      className="form-input"
                      style={{ cursor: 'pointer', fontWeight: 600, background: 'var(--bg-input)' }}
                      value={salesOrders.some(so => so.salesOrderRef === form.salesOrderRef) ? form.salesOrderRef : (form.salesOrderRef ? '__custom__' : '')}
                      onChange={e => handleSelectSalesOrder(e.target.value)}
                    >
                      <option value="">-- Select Sales Order to Auto-fill --</option>
                      {salesOrders.map(so => (
                        <option key={so.salesOrderRef} value={so.salesOrderRef}>
                          {so.salesOrderRef} — {so.customerName} ({so.items?.length || 0} items)
                        </option>
                      ))}
                      <option value="__custom__">-- Manual / Custom SO Reference --</option>
                    </select>

                    {(!salesOrders.some(so => so.salesOrderRef === form.salesOrderRef) || form.salesOrderRef === '__custom__') && (
                      <input
                        className="form-input"
                        style={{ marginTop: '8px' }}
                        value={form.salesOrderRef === '__custom__' ? '' : form.salesOrderRef}
                        onChange={e => setForm(p => ({ ...p, salesOrderRef: e.target.value }))}
                        placeholder="Type custom SO Ref (e.g. SO-2026-001)"
                      />
                    )}
                  </div>
                </div>
                <div style={{ marginBottom: '14px' }}>
                  <label className="form-label">Delivery Address</label>
                  <textarea className="form-input" rows={2} value={form.customerAddress} onChange={e => setForm(p => ({ ...p, customerAddress: e.target.value }))} placeholder="Full delivery address..." />
                </div>

                {/* Items */}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label className="form-label" style={{ margin: 0 }}>Items</label>
                    <button type="button" onClick={addItem} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '6px', color: 'var(--primary)', cursor: 'pointer', padding: '3px 10px', fontSize: '0.82rem' }}>+ Add Item</button>
                  </div>
                  {form.items.map((item, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 70px auto', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={item.modelNumber} onChange={e => updateItem(idx, 'modelNumber', e.target.value)} placeholder="Model No." />
                      <input className="form-input" style={{ fontSize: '0.82rem' }} value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} placeholder="Description" />
                      <input className="form-input" style={{ fontSize: '0.82rem' }} type="number" min={1} value={item.quantity} onChange={e => updateItem(idx, 'quantity', Number(e.target.value))} placeholder="Qty" />
                      <select className="form-input" style={{ fontSize: '0.82rem' }} value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)}>
                        <option>pcs</option><option>kg</option><option>meters</option><option>boxes</option>
                      </select>
                      {form.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} style={{ background: 'rgba(239,68,68,0.1)', border: 'none', color: '#ef4444', cursor: 'pointer', borderRadius: '4px', padding: '4px 6px' }}><HiOutlineX /></button>}
                    </div>
                  ))}
                </div>

                {/* Status */}
                <div style={{ marginBottom: '14px' }}>
                  <label className="form-label">Workflow Stage</label>
                  <select className="form-input" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>

                {/* Step 1 Footer Buttons */}
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  <button type="button" className="btn btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={submitting}
                      className="btn btn-secondary"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(99,102,241,0.1)', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}
                    >
                      💾 Save as Draft
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        if (!form.customerName?.trim()) {
                          toast.error('Please enter Customer Name to proceed');
                          return;
                        }
                        setModalStep(2);
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      Next: 📦 Step 2 (Packing Details) ➔
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2: PACKING DETAILS */}
            {modalStep === 2 && (
              <div>
                <div style={{ background: 'rgba(59,130,246,0.03)', borderRadius: '10px', border: '1px solid rgba(59,130,246,0.2)', padding: '16px', marginBottom: '14px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#3b82f6', marginBottom: '12px' }}>
                    📦 Step 2: Packing Details & Box Counts
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Total Box Count</label>
                      <input className="form-input" type="number" min={0} value={form.totalBoxes} onChange={e => setForm(p => ({ ...p, totalBoxes: e.target.value }))} placeholder="0" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Total Weight</label>
                      <input className="form-input" value={form.totalWeight} onChange={e => setForm(p => ({ ...p, totalWeight: e.target.value }))} placeholder="e.g. 45 KG" />
                    </div>
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Packing Details / Notes</label>
                    <textarea className="form-input" rows={2} value={form.packingDetails} onChange={e => setForm(p => ({ ...p, packingDetails: e.target.value }))} placeholder="Packaging specifications, labels, strapping..." />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setModalStep(1)}>
                    ⬅ Back to Step 1 (Assembly)
                  </button>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={submitting}
                      className="btn btn-secondary"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(99,102,241,0.1)', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}
                    >
                      💾 Save as Draft
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => setModalStep(3)}>
                      Next: 🧾 Step 3 (Billing & Invoices) ➔
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3: BILLING & REMARKS */}
            {modalStep === 3 && (
              <div>
                <div style={{ background: 'rgba(16,185,129,0.03)', borderRadius: '10px', border: '1px solid rgba(16,185,129,0.2)', padding: '16px', marginBottom: '14px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#10b981', marginBottom: '12px' }}>
                    🧾 Step 3: Remarks & Documentation
                  </div>
                  <div>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Remarks / Order Notes</label>
                    <textarea className="form-input" rows={3} value={form.remarks} onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Any special customer instructions..." />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setModalStep(2)}>
                    ⬅ Back to Step 2 (Packing)
                  </button>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={submitting}
                      className="btn btn-secondary"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(99,102,241,0.1)', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}
                    >
                      💾 Save as Draft
                    </button>
                    <button type="button" className="btn btn-primary" onClick={() => setModalStep(4)}>
                      Next: 🚚 Step 4 (Dispatch & Logistics) ➔
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4: LOGISTICS & DISPATCH */}
            {modalStep === 4 && (
              <div>
                <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid var(--border)', padding: '16px', marginBottom: '14px' }}>
                  <div style={{ fontWeight: 700, marginBottom: '12px', fontSize: '0.92rem' }}>🚚 Step 4: Courier Logistics, LR & POD</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Courier / Transporter</label>
                      <input className="form-input" value={form.courier} onChange={e => setForm(p => ({ ...p, courier: e.target.value }))} placeholder="DTDC, BlueDart, V-Trans..." />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Tracking Number</label>
                      <input className="form-input" value={form.trackingNumber} onChange={e => setForm(p => ({ ...p, trackingNumber: e.target.value }))} placeholder="AWB / Tracking ID" />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>LR Number</label>
                      <input className="form-input" value={form.lrNumber} onChange={e => setForm(p => ({ ...p, lrNumber: e.target.value }))} placeholder="Lorry Receipt No." />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>LR Copy (PDF / Scan / Link)</label>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.lrCopyUrl} onChange={e => setForm(p => ({ ...p, lrCopyUrl: e.target.value }))} placeholder="Paste link or upload..." />
                        <label style={{ background: 'var(--primary)', color: '#fff', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          <HiOutlineUpload /> Upload
                          <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => handleFileUpload(e, 'lrCopyUrl')} />
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>POD Receipt (PDF / Scan / Link)</label>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <input className="form-input" style={{ fontSize: '0.82rem' }} value={form.podUrl} onChange={e => setForm(p => ({ ...p, podUrl: e.target.value }))} placeholder="Paste link or upload..." />
                        <label style={{ background: 'var(--primary)', color: '#fff', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          <HiOutlineUpload /> Upload
                          <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => handleFileUpload(e, 'podUrl')} />
                        </label>
                      </div>
                    </div>
                  </div>

                  {form.status === 'hold' && (
                    <div style={{ marginTop: '12px' }}>
                      <label className="form-label" style={{ fontSize: '0.8rem', color: '#ef4444' }}>Hold Reason *</label>
                      <input className="form-input" value={form.holdReason} onChange={e => setForm(p => ({ ...p, holdReason: e.target.value }))} placeholder="Reason for hold..." />
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between', marginTop: '20px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setModalStep(3)}>
                    ⬅ Back to Step 3 (Billing)
                  </button>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={submitting}
                      className="btn btn-secondary"
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(99,102,241,0.1)', borderColor: '#6366f1', color: '#6366f1', fontWeight: 600 }}
                    >
                      💾 Save as Draft
                    </button>
                    <button type="button" className="btn btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" disabled={submitting} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none' }}>
                      <HiOutlineSave /> {submitting ? 'Saving...' : editingId ? 'Update Entry' : '💾 Save & Complete Order'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </ModalOverlay>
      )}

      {/* Pre-Dispatch Quality Check (shared component, meters only) */}
      {showQcModal && qcOrder && (
        <DispatchQcModal
          order={qcOrder}
          onClose={handleCloseQcModal}
          onSaved={fetchData}
        />
      )}
    </DashboardLayout>
  );
}

